import { inject, Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, defaultIfEmpty, filter, map, mergeMap, switchMap, take, toArray } from 'rxjs/operators';

import { ApContentService } from '@ap/content';
import { ProfileMenu } from '@ap/header';
import { BerichtbeheerResource, BerichtInformatie, Status } from '@ap/interface-apic-berichtbeheer';
import { SessiebeheerResource, SessionStatus } from '@ap/interface-apic-sessiebeheer';
import { MijnOverzichtResource, OpenstaandeOpdrachten } from '@ap/interface-apix-mijn-overzicht';
import { MijnAuthSessieManagerService } from '@ap/mijn-auth-sessie-manager';
import { MijnConditiesService } from '@ap/mijn-condities';
import { MijnLaunchDarklyService } from '@ap/mijn-kanaalconfiguratie';
import { MijnNavigatieService } from '@ap/mijn-navigatie';
import { ApPersistenceService } from '@ap/persistence';

import { MijnTemplateNavigation } from '../mijn-template.navigation';

import { Breadcrumb } from './breadcrumb/breadcrumb.model';
import content from './config/header-content.json';
import { getProfileHeader } from './config/header.config';
import { NEXTBESTACTION_SESSIONNAME } from './mijn-overzicht-template.constant';
import { MijnFeedbackForm, mijnFeedbackFormDefault } from './mijn-overzicht-template.model';

@Injectable()
export class MijnOverzichtTemplateService {
    private readonly berichtbeheerResource: BerichtbeheerResource = inject(BerichtbeheerResource);
    private readonly mijnNavigatieService: MijnNavigatieService = inject(MijnNavigatieService);
    private readonly mijnTemplateNavigation: MijnTemplateNavigation = inject(MijnTemplateNavigation);
    private readonly launchDarklyService: MijnLaunchDarklyService = inject(MijnLaunchDarklyService);
    private readonly mijnOverzichtResource: MijnOverzichtResource = inject(MijnOverzichtResource);
    private readonly persistenceService: ApPersistenceService = inject(ApPersistenceService);
    private readonly sessiebeheerResource: SessiebeheerResource = inject(SessiebeheerResource);
    private readonly mijnConditiesService: MijnConditiesService = inject(MijnConditiesService);
    private readonly contentService: ApContentService = inject(ApContentService);
    private readonly mijnKlantLoginService: MijnAuthSessieManagerService = inject(MijnAuthSessieManagerService);

    private readonly nrOfUnreadMessages = new Subject<number>();
    private readonly currentSession = '0';

    public constructor() {
        this.contentService.addContentItems(content);
    }

    private static hasAllowedPriority(berichtInformatie: BerichtInformatie): boolean {
        return berichtInformatie.prioriteit < 21 || berichtInformatie.prioriteit > 29;
    }

    public getNrOfUnreadMessages(): Subject<number> {
        return this.nrOfUnreadMessages;
    }

    public loadNrOfUnreadMessages(): void {
        this.berichtbeheerResource
            .getBerichtenInformatie(Status.ONGELEZEN)
            .pipe(
                mergeMap((pagina) => pagina.berichten_informatie),
                filter(MijnOverzichtTemplateService.hasAllowedPriority),
                toArray(),
                map((berichten) => berichten.length),
                catchError(() => of(0))
            )
            .subscribe((result: number) => {
                this.nrOfUnreadMessages.next(result);
            });
    }

    public getNrOfSendableOrders(): Observable<number> {
        return this.mijnOverzichtResource.getOpenstaandeOpdrachten().pipe(
            map((result: OpenstaandeOpdrachten) => +result.aantal_te_verzenden_opdrachten),
            catchError(() => of(0))
        );
    }

    public terminateUserSession(): Observable<void> {
        return this.mijnKlantLoginService.isAuthenticated$.pipe(
            take(1),
            switchMap((authenticated) => {
                if (authenticated) {
                    return this.mijnKlantLoginService.logout();
                }
                return this.sessiebeheerResource.patchSession(this.currentSession, { status: SessionStatus.TERMINATED });
            }),
            catchError(() => {
                console.error('error logging out');
                return of();
            })
        );
    }

    public getDynamicBreadcrumb(): Breadcrumb {
        const isMijnUrl = this.mijnTemplateNavigation.isMijnUrl();
        const history = this.mijnNavigatieService.getHistory();
        if (isMijnUrl && history?.enabled && history.routes.length > 1) {
            const previousRoute = history.routes[history.routes.length - 2];

            return {
                label: previousRoute.breadcrumb,
                path: previousRoute.url,
                dynamic: true
            };
        }
        return null;
    }

    public clearDynamicBreadcrumbs(): void {
        this.mijnNavigatieService.clearHistory();
    }

    public clearNBASession(): void {
        this.persistenceService.clearSession(NEXTBESTACTION_SESSIONNAME);
    }

    public getMijnFeedbackSettings(key: string): Observable<MijnFeedbackForm | null> {
        return this.launchDarklyService.getFlagValue(key, mijnFeedbackFormDefault).pipe(
            take(1),
            map(
                (json) =>
                    ({
                        moodId: json.moodId,
                        commentsId: json.commentsId,
                        channelId: json.channelId
                    }) as MijnFeedbackForm
            ),
            filter((form) => form !== null && form.commentsId !== undefined && form.moodId !== undefined && form.channelId !== undefined),
            defaultIfEmpty(null)
        );
    }

    public setupProfileHeader(userName: string | null, companyName: string | null, unreadMessagesSize: number): ProfileMenu {
        const header = getProfileHeader(userName, companyName, unreadMessagesSize);

        header.groups = header.groups
            .filter((profileGroup) => {
                profileGroup.items = profileGroup.items
                    .filter((menuItem) => {
                        if (!menuItem.link && !menuItem.routeKey) {
                            console.error(`No link target provided for ${menuItem.title}!`); //NOSONAR
                        }
                        return this.mijnConditiesService.checkContentKeyCondition(menuItem.title);
                    })
                    .map((menuItem) => {
                        menuItem.title = this.contentService.get(menuItem.title);
                        menuItem.subtitle = this.contentService.get(menuItem.subtitle);
                        menuItem.routeKey = this.contentService.get(menuItem.routeKey);

                        return menuItem;
                    });

                return profileGroup.items.length > 0;
            })
            .map((profileGroup) => {
                profileGroup.title = this.contentService.get(profileGroup.title);

                return profileGroup;
            });

        return header;
    }
}
